import { makeStyles } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import Loading from "../../../../common-components/Loading";
import { getPMRDetailsAPI } from "../../../../api/reports-api";
import PaginationBar, { usePagination, PAGE_SIZE_OPTIONS } from "../labor-utilization/Pagination";
import { useLuTableSortFilter, LuSortFilterTh } from "../labor-utilization/tableSortFilterUtils";
import {
  TABLE_FONT,
  CELL_FG,
  EVEN_BG,
  ODD_BG,
  TH_BG_BAND,
  CELL_FG_STRONG,
} from "../../reportTableConfig";

/** Field-name map: PMR API row/total shape → this page's internal row shape. */
function mapPmrRow(r) {
  return {
    projectId:        r.projId,
    projectName:      r.projectName,
    popStart:         r.popStartDate,
    popEnd:           r.popEndDate,
    active:           r.active,
    contractType:     r.contractType,
    value:            r.contractValue,
    funding:          r.fundedValue,
    atRisk:           r.fundedRisk,
    itdRevenue:       r.itdRevenueActuals,
    unfundedBacklog:  r.unfundedBacklog,
    remainingFunding: r.currentRemainingFunding,
    pctFundingUsed:   r.pctFundingUsed,
    billed:           r.billedToDate,
    balanceDue:       r.balanceDue,
    unbilled:         r.unbilledBalance,
  };
}

function mapPmrTotals(t) {
  if (!t) return null;
  return {
    value:                  t.contractValue,
    funding:                t.fundedValue,
    atRisk:                 t.fundedRisk,
    itdRevenue:             t.itdRevenueActuals,
    unfundedBacklog:        t.unfundedBacklog,
    remainingFunding:       t.currentRemainingFunding,
    pctFundingUsedDisplay:  t.pctFundingUsed,
    billed:                 t.billedToDate,
    balanceDue:             t.balanceDue,
    unbilled:               t.unbilledBalance,
  };
}

/** Recomputed from the currently filtered rows (server totals cover ALL rows,
    which no longer matches once a column filter narrows the visible set —
    same convention Labor Utilization's Summary/FutureEmplNotes tabs use). */
function computeClientTotals(rows) {
  const sum = (k) => rows.reduce((s, r) => s + (Number(r[k]) || 0), 0);
  const funding = sum("funding");
  const itdRevenue = sum("itdRevenue");
  return {
    value: sum("value"),
    funding,
    atRisk: sum("atRisk"),
    itdRevenue,
    unfundedBacklog: sum("unfundedBacklog"),
    remainingFunding: sum("remainingFunding"),
    pctFundingUsedDisplay: funding ? (itdRevenue / funding) * 100 : null,
    billed: sum("billed"),
    balanceDue: sum("balanceDue"),
    unbilled: sum("unbilled"),
  };
}

const HEADER_GROUP = TH_BG_BAND;
const NEG_C = "#D64550";
const TOTAL_FG = CELL_FG_STRONG;
const TOTAL_BG     = "#F0FFF4";
const TAB_ACTIVE   = "#4F46E5";
const FONT = TABLE_FONT;

const SUB_TABS = [
  { key: "contract", label: "Contract Information" },
  { key: "funding",  label: "Funding Information" },
  { key: "ar",       label: "A/R Information" },
];

/* isText drives both the sort comparator (string vs numeric) and which kind
   of column-filter popup (text substring vs numeric operator) each column gets. */
const COLUMNS = {
  contract: [
    { key: "projectId",         label: "Project Id",                  align: "left",  type: "text",      isText: true },
    { key: "projectName",       label: "Project Name",                align: "left",  type: "text",      isText: true },
    { key: "popStart",          label: "POP Start Date",              align: "left",  type: "dateShort", isText: true },
    { key: "popEnd",            label: "POP End Date",                align: "left",  type: "dateShort", isText: true },
    { key: "active",            label: "Active",                      align: "left",  type: "text",      isText: true },
    { key: "contractType",      label: "Contract Type",               align: "left",  type: "text",      isText: true },
    { key: "value",             label: "Contract Value",              align: "right", type: "currency",  totalKey: "value"             },
    { key: "funding",           label: "Funded Value",                align: "right", type: "currency",  totalKey: "funding"           },
    { key: "atRisk",            label: "Funded Risk",                 align: "right", type: "currency",  totalKey: "atRisk"            },
    { key: "itdRevenue",        label: "ITD Revenue Actuals",         align: "right", type: "currency",  totalKey: "itdRevenue"        },
    { key: "unfundedBacklog",   label: "Unfunded Backlog",            align: "right", type: "currency",  totalKey: "unfundedBacklog"   },
    { key: "remainingFunding",  label: "Current Remaining Funding",   align: "right", type: "currency",  totalKey: "remainingFunding"  },
  ],
  funding: [
    { key: "projectId",         label: "Project Id",                  align: "left",  type: "text",      isText: true },
    { key: "contractType",      label: "Contract Type",               align: "left",  type: "text",      isText: true },
    { key: "value",             label: "Contract Value",              align: "right", type: "currency",  totalKey: "value"             },
    { key: "funding",           label: "Funded Value",                align: "right", type: "currency",  totalKey: "funding"           },
    { key: "atRisk",            label: "Funded Risk",                 align: "right", type: "currency",  totalKey: "atRisk"            },
    { key: "unfundedBacklog",   label: "Unfunded Backlog",            align: "right", type: "currency",  totalKey: "unfundedBacklog"   },
    { key: "remainingFunding",  label: "Current Remaining Funding",   align: "right", type: "currency",  totalKey: "remainingFunding"  },
    { key: "pctFundingUsed",    label: "% Funding Used",              align: "right", type: "pct",       totalKey: "pctFundingUsedDisplay" },
  ],
  ar: [
    { key: "projectId",         label: "Project Id",                  align: "left",  type: "text",      isText: true },
    { key: "itdRevenue",        label: "ITD Revenue Actuals",         align: "right", type: "currency",  totalKey: "itdRevenue"        },
    { key: "unfundedBacklog",   label: "Unfunded Backlog",            align: "right", type: "currency",  totalKey: "unfundedBacklog"   },
    { key: "remainingFunding",  label: "Current Remaining Funding",   align: "right", type: "currency",  totalKey: "remainingFunding"  },
    { key: "pctFundingUsed",    label: "% Funding Used",              align: "right", type: "pct",       totalKey: "pctFundingUsedDisplay" },
    { key: "billed",            label: "Billed to Date",              align: "right", type: "currency",  totalKey: "billed"            },
    { key: "balanceDue",        label: "Balance Due",                 align: "right", type: "currency",  totalKey: "balanceDue"        },
    { key: "unbilled",          label: "Unbilled Balance",            align: "right", type: "currency",  totalKey: "unbilled"          },
  ],
};

const TOOLTIP_FIELDS = [
  { key: "projectId",    label: "Project Id"       },
  { key: "projectName",  label: "Project Name"     },
  { key: "popStart",     label: "POP Start Date",  type: "dateShort" },
  { key: "popEnd",       label: "POP End Date",    type: "dateShort" },
  { key: "active",       label: "Active"           },
  { key: "contractType", label: "Contract Type"    },
];

function formatDateShort(iso) {
  if (!iso) return "";
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" });
}
function formatDateLong(iso) {
  if (!iso) return "";
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}
function fmtCurrency(v) {
  if (v == null || v === "") return "";
  const n = Number(v);
  const abs = Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `($${abs})` : `$${abs}`;
}
function fmtPct(v) {
  const n = Number(v);
  const abs = Math.abs(n).toFixed(1);
  return n < 0 ? `(${abs}%)` : `${abs}%`;
}
function formatCell(row, col) {
  const v = row[col.key];
  if (v == null || v === "") return "";
  switch (col.type) {
    case "dateShort":  return formatDateShort(v);
    case "dateLong":   return formatDateLong(v);
    case "currency":   return fmtCurrency(v);
    case "pct":        return fmtPct(v);
    default:           return String(v);
  }
}
function formatTotal(col, totals) {
  if (!col.totalKey || !totals) return "";
  const v = totals[col.totalKey];
  if (v == null) return "";
  if (col.type === "pct") return fmtPct(v);
  return fmtCurrency(v);
}
// Matches ForecastAnalysis's negative-value treatment: currency/pct cells go red when negative,
// everything else (text, dates) keeps the normal cell color.
function negColor(v, type, fallback) {
  if (type !== "currency" && type !== "pct") return fallback;
  const n = Number(v);
  return Number.isFinite(n) && n < 0 ? NEG_C : fallback;
}

/* ── Inline style helpers ── */
const thStyle = (align) => ({
  position: "sticky",
  top: 0,
  zIndex: 3,
  height: 34,
  background: HEADER_GROUP,
  color: "rgba(255,255,255,.75)",
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  textAlign: align,
  padding: "0 12px",
  whiteSpace: "nowrap",
  border: "none",
  boxSizing: "border-box",
  verticalAlign: "middle",
  boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
});

const tdStyle = (bg, align) => ({
  padding: "7px 12px",
  textAlign: align,
  fontSize: 12,
  color: CELL_FG,
  whiteSpace: "nowrap",
  overflow: "hidden",
  borderBottom: "1px solid #E2E8F0",
  background: bg,
  verticalAlign: "middle",
  fontVariantNumeric: "tabular-nums",
  boxSizing: "border-box",
});

function RowTooltip({ row, classes }) {
  return (
    <div className={classes.tooltip}>
      {TOOLTIP_FIELDS.map((f) => (
        <div key={f.key} className={classes.tooltipRow}>
          <span className={classes.tooltipLabel}>{f.label}</span>
          <span className={classes.tooltipValue}>
            {formatCell(row, { key: f.key, type: f.type || "text" })}
          </span>
        </div>
      ))}
    </div>
  );
}

function PmrDetailsGrid({ columns, pagedRows, allVisibleRows, totals, classes, hoveredId, onHover, thProps }) {
  return (
    <div style={{ position: "relative" }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", borderSpacing: 0, fontFamily: FONT, fontSize: 12, width: "100%", minWidth: 960 }}>
          <thead>
            <tr style={{ height: 34 }}>
              {columns.map((col) => (
                <LuSortFilterTh
                  key={col.key}
                  col={col}
                  style={thStyle(col.align)}
                  align={col.align}
                  {...thProps}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((row, i) => {
              const bg = hoveredId === row.projectId ? "#DBEAFE" : (i % 2 === 0 ? EVEN_BG : ODD_BG);
              return (
                <tr
                  key={row.projectId}
                  style={{ height: 36, cursor: "pointer" }}
                  onMouseEnter={() => onHover(row.projectId)}
                  onMouseLeave={() => onHover(null)}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      style={{
                        ...tdStyle(bg, col.align),
                        color: negColor(row[col.key], col.type, CELL_FG),
                      }}
                    >
                      {formatCell(row, col)}
                    </td>
                  ))}
                </tr>
              );
            })}
            {/* Total row — reflects the filtered set, not just the current page */}
            <tr style={{ height: 36 }}>
              <td style={{ ...tdStyle(TOTAL_BG, "left"), color: TOTAL_FG, fontWeight: 700, borderBottom: "1px solid #CBD5E1" }}>
                Total
              </td>
              {columns.slice(1).map((col) => (
                <td
                  key={col.key}
                  style={{
                    ...tdStyle(TOTAL_BG, col.align),
                    color: negColor(totals?.[col.totalKey], col.type, TOTAL_FG),
                    fontWeight: 600,
                    borderBottom: "1px solid #CBD5E1",
                  }}
                >
                  {formatTotal(col, totals)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      {hoveredId && allVisibleRows.find((r) => r.projectId === hoveredId) && (
        <RowTooltip row={allVisibleRows.find((r) => r.projectId === hoveredId)} classes={classes} />
      )}
    </div>
  );
}

const useStyles = makeStyles(() => ({
  root: { fontFamily: FONT, paddingBottom: 24 },

  tableCard: {
    background: "#fff",
    borderRadius: 12,
    border: "1px solid #EBEBEB",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
    overflow: "hidden",
    marginTop: 20,
    marginBottom: 20,
  },

  subTabBar: {
    background: "#fff",
    borderBottom: "1px solid #E5E7EB",
    display: "flex",
    flexWrap: "wrap",
    padding: "0 16px",
    gap: "4px 0",
  },
  subTab: {
    padding: "10px 14px",
    fontSize: 13,
    fontWeight: 500,
    color: "#374151",
    cursor: "pointer",
    whiteSpace: "nowrap",
    border: "none",
    background: "none",
    transition: "color 0.15s, box-shadow 0.15s",
    "&:hover": { color: TAB_ACTIVE },
  },
  subTabActive: {
    color: TAB_ACTIVE,
    fontWeight: 600,
    boxShadow: `inset 0 -2px 0 0 ${TAB_ACTIVE}`,
  },

  gridArea: { padding: "16px" },

  tooltip: {
    position: "absolute",
    top: 8,
    right: 8,
    zIndex: 10,
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    padding: "12px 16px",
    minWidth: 280,
    pointerEvents: "none",
    fontFamily: FONT,
  },
  tooltipRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    padding: "4px 0",
    fontSize: 11,
    borderBottom: "1px solid #F1F5F9",
    "&:last-child": { borderBottom: "none" },
  },
  tooltipLabel: { color: "#64748B", fontWeight: 600, flexShrink: 0 },
  tooltipValue: { color: "#111827", fontWeight: 600, textAlign: "right" },
  errorText: { padding: "24px", color: "#DC2626", fontSize: 13, fontFamily: FONT },
}));

export default function OptionalForPMRDetails({ filters = {} }) {
  const classes      = useStyles();
  const [activeSubTab, setActiveSubTab] = useState("contract");
  const [hoveredId,    setHoveredId]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [rows,    setRows]    = useState([]);
  const [totals,  setTotals]  = useState(null);
  const columns = COLUMNS[activeSubTab];

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getPMRDetailsAPI(filters)
      .then((res) => {
        if (cancelled) return;
        const d = res?.data;
        if (d) {
          setRows((d.rows || []).map(mapPmrRow));
          setTotals(mapPmrTotals(d.total));
        } else {
          setError("Failed to load data.");
        }
      })
      .catch(() => { if (!cancelled) setError("Failed to load data."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [JSON.stringify(filters)]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    sortKeys,
    sortedRows,
    filteredRows,
    hasTableFilters,
    openFilterKey,
    onThClick,
    getSortColumnLabel,
    hasFilterValue,
    toggleFilter,
    filterBtnRefs,
    renderFilterPanel,
  } = useLuTableSortFilter(columns, rows);

  const { pagedItems: pagedRows, resetPage, ...pagination } = usePagination(
    sortedRows,
    PAGE_SIZE_OPTIONS[0],
  );

  // Reset to page 1 and clear the hover tooltip whenever the sub-tab or the
  // underlying dataset changes (new column set / new filters response).
  useEffect(() => { resetPage(); setHoveredId(null); }, [activeSubTab, rows]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <Loading />;
  if (error) return <div className={classes.errorText}>{error}</div>;

  const displayTotals = hasTableFilters ? computeClientTotals(filteredRows) : totals;

  const thProps = {
    sortKeys,
    onThClick,
    getSortColumnLabel,
    openFilterKey,
    hasFilterValue,
    toggleFilter,
    filterBtnRefs,
  };

  return (
    <div className={classes.root}>
      <div className={classes.tableCard}>
        {/* Sub-tab bar */}
        <div className={classes.subTabBar}>
          {SUB_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`${classes.subTab} ${activeSubTab === tab.key ? classes.subTabActive : ""}`}
              onClick={() => setActiveSubTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className={classes.gridArea}>
          <PmrDetailsGrid
            columns={columns}
            pagedRows={pagedRows}
            allVisibleRows={sortedRows}
            totals={displayTotals}
            classes={classes}
            hoveredId={hoveredId}
            onHover={setHoveredId}
            thProps={thProps}
          />
        </div>
        <PaginationBar
          {...pagination}
          totalItems={sortedRows.length}
          onPageChange={pagination.goToPage}
          onPageSizeChange={pagination.handlePageSizeChange}
        />
      </div>
      {renderFilterPanel()}
    </div>
  );
}
